import { Dispatch } from 'redux';

import { logger } from 'shared/logging';
import statusCodes from 'constants/statusCodes';
import { actionForSSRDataLayer } from '@nykaa/data-layer/store/action';
import appEventTypes from 'shared/store/app/actionTypes';
import { pushData, pushEvent } from '@nykaa/data-layer/utils';
import { isValidObject } from '@nykaa/utils/validations';
import { getHomePageWidgets as getBannerWidgets } from 'shared/store/home/service';
import { getVariantsForPageType } from 'shared/helpers/experimentV2';
import {
  generateProductSchema,
  generateBreadcrumbSchema,
} from '@nykaa/jsonld-shared/store/action';
import { getSchemaData } from '@nykaa/jsonld-shared/helper';
import footerActionTypes from 'shared/store/footer/actionTypes';
import actionTypes from './actionTypes';
import {
  getDataLayerForProduct,
  extractProductJourneyData,
  getCatgoryLevelId,
} from './helpers';
import {
  getProduct,
  getProductOffersAndCoupons,
  getDynamicFooterLinks,
} from './service';
import {
  FetchBannerParams,
  FetchProductParams,
  Offer,
  OffersParams,
  Product,
  VariantChange,
} from './types';
import { defaultDataLayer } from 'constants/dataLayer';
import { getBreadcrumbsWithTimeout } from 'store/app/service';
import { selectCurrentSku } from './selector';
import {
  SHADE_CLICKED_EVENT,
  SIZE_CLICKED_EVENT,
  DYNAMIC_FOOTER_LINK_API_ERR_MESSAGE,
} from 'shared/constants';
import { getVariantType, isSize } from 'components/Recommendations/helpers';
import actions from '../../../shared/store/coupons/actionTypes';

const PD_PAGE = 'pd-page';

interface ResolutionData {
  productData: Product | null;
  errorCode: number | null;
}

export const fetchBreadcrumbs =
  (id: string, timeout: number = 100) =>
  (dispatch: Dispatch) =>
    getBreadcrumbsWithTimeout(id, timeout).then(
      (res: { key: string; value: string }[]) => {
        dispatch({
          type: actionTypes.SET_BREADCRUMB,
          payload: res,
        });
        return res;
      }
    );

export const fetchProduct =
  (params: FetchProductParams) => (dispatch: any, getState: Function) => {
    // FIXME: type for dispatch
    dispatch({
      type: actionTypes.FETCH_PRODUCT_DATA_PROGRESS,
    });

    return getProduct(params, getState())
      .then((productData) => {
        if (params.isHybridProduct) {
          dispatch({
            type: actionTypes.FETCH_HYBIRD_PRODUCT_DATA_SUCCESS,
            payload: productData,
          });
        } else {
          dispatch({
            type: actionTypes.FETCH_PRODUCT_DATA_SUCCESS,
            payload: productData,
          });
        }
        return {
          productData,
          errorCode: null,
        };
      })
      .catch((err) => {
        let status = statusCodes.ERROR;
        if (err.isAxiosError && err.response?.status) {
          status = err.response.status;
        }

        logger.error(
          err,
          `Error in Fetch Product action ${JSON.stringify(params)}`,
          { code: status }
        );
        dispatch({
          type: appEventTypes.APP_REDUCER_STATUS_CODE,
          payload: status,
        });
        dispatch({
          type: actionTypes.FETCH_PRODUCT_DATA_FAILED,
        });

        return {
          productData: null,
          errorCode: status,
        };
      })
      .then(async ({ productData, errorCode }: ResolutionData) => {
        const currentState = getState();
        const currentSku = selectCurrentSku(currentState);
        const dataLayerForProduct = getDataLayerForProduct(
          productData,
          errorCode,
          params,
          currentSku
        );
        if (__SERVER__) {
          dispatch(actionForSSRDataLayer(dataLayerForProduct));
        } else {
          const defaultData = defaultDataLayer();
          pushData({
            ...defaultData,
            ...dataLayerForProduct,
            spaPageView: Date.now(),
            event: 'spaPageView',
          });
        }
        if (productData || currentSku) {
          const schemaData = getSchemaData({
            ...currentSku,
            topReviews: productData?.topReviews || [],
          });
          dispatch(generateProductSchema(schemaData));
          try {
            const primaryCategories = productData?.primaryCategories || {};
            const isBreadcrumbEnabled =
              currentState.remoteConfig?.switches?.isPdBreadcrumbEnabled;
            if (isValidObject(primaryCategories) && isBreadcrumbEnabled) {
              const categoryId = getCatgoryLevelId(primaryCategories);
              if (categoryId) {
                const timeout =
                  currentState.remoteConfig?.configs?.pdBreadcrumbsTimeout ||
                  100;
                const breadcrumbs = await dispatch(
                  fetchBreadcrumbs(categoryId, timeout)
                );
                if (breadcrumbs?.length) {
                  dispatch(generateBreadcrumbSchema(breadcrumbs));
                }
              }
            }
          } catch (error) {
            logger.error(
              error,
              'Error in Fetch Product action: fetchBreadcrumbs'
            );
          }
        }

        return { productData, errorCode };
      });
  };

export const changeCurrentVariant =
  (payload: VariantChange, product?: Product | null) => (dispatch: any) => {
    dispatch({
      type: actionTypes.CHANGE_PRODUCT_VARIANT,
      payload,
    });
    if (isValidObject(product)) {
      const variantType = getVariantType(product?.variantType);
      const event = isSize(variantType)
        ? SIZE_CLICKED_EVENT
        : SHADE_CLICKED_EVENT;
      requestAnimationFrame(() => {
        pushEvent(event, { selectedVariant: product });
      });
    }
  };

export const fetchProductOffers =
  (
    params: OffersParams,
    isOfferApiV2Enabled: boolean,
    defaultDescForCoupons?: string,
    bestPriceEnabled?: boolean
  ) =>
  (dispatch: any) => {
    dispatch({
      type: actionTypes.FETCH_OFFERS_PROGRESS,
      payload: params,
    });
    return getProductOffersAndCoupons(
      params,
      isOfferApiV2Enabled,
      defaultDescForCoupons,
      bestPriceEnabled
    )
      .then((offersAndCoupons) => {
        dispatch({
          type: actionTypes.FETCH_COUPONS_AND_OFFERS_SUCCESS,
          payload: offersAndCoupons,
        });
        dispatch({
          type: actions.FETCH_COUPONS_AND_OFFERS_SUCCESS,
          payload: offersAndCoupons?.coupons,
        });
        dispatch({
          type: actionTypes.UPDATE_PERSONALIZED_BESTPRICE_OFFERS,
          payload: offersAndCoupons?.personalizedBestOfferDetail?.offers,
        });
      })
      .catch((err) => {
        dispatch({
          type: actionTypes.FETCH_OFFERS_FAILED,
        });
        return Promise.reject(err);
      });
  };

export const trackProductJourney =
  (queryString: string, id: number) => (dispatch: Dispatch) => {
    // TODO: extract tracking data from url string
    const trackingData = extractProductJourneyData(queryString, id);
    if (trackingData) {
      if (__SERVER__) {
        dispatch(actionForSSRDataLayer(trackingData));
      } else {
        pushData(trackingData, true);
      }
    }
  };

export const additionalOfferForProduct = (payload: Offer) => ({
  type: actionTypes.PRODUCT_ADDITIONAL_OFFER,
  payload: payload,
});

export const fetchBanners =
  (params: FetchBannerParams, isPaginationEnabled?: boolean) =>
  (dispatch: Dispatch, getState: any) => {
    const PAGE_TYPE_FOR_PD_BANNERS = PD_PAGE;
    const state = getState();
    const variants = getVariantsForPageType(state, PAGE_TYPE_FOR_PD_BANNERS);
    const { lang } = params;
    const requestParams = {
      page_type: PAGE_TYPE_FOR_PD_BANNERS,
      page_data: params.productId,
      dn_platform: 'mweb',
      page_section: '*',
      ...variants,
      ...(lang && { lang }),
      api_version: isPaginationEnabled ? 'V2' : '',
    };

    return getBannerWidgets(requestParams, '', isPaginationEnabled)
      .then(({ widgets, remaining_inventories_ids }) => {
        if (Array.isArray(widgets)) {
          dispatch({
            type: actionTypes.BANNERS_SUCCESS,
            payload: {
              widgets,
              remaining_inventories_ids,
              queryParamsObject: { ...requestParams },
            },
          });
        }
      })
      .catch(() => {
        dispatch({
          type: 'pd-banner-fail',
        });
      });
  };

export const productUnMount = () => ({
  type: actionTypes.PRODUCT_UNMOUNT,
});

export const fetchCategoryWidgets =
  (isPaginationEnabled?: boolean) => (dispatch: Dispatch) => {
    const requestParams = {
      page_type: PD_PAGE,
      page_data: 'default',
      page_section: '*',
      api_version: isPaginationEnabled ? 'V2' : '',
    };

    return getBannerWidgets(requestParams, '', isPaginationEnabled)
      .then(({ widgets, remaining_inventories_ids }) => {
        if (Array.isArray(widgets)) {
          dispatch({
            type: actionTypes.CATEGORY_WIDGET_SUCCESS,
            payload: {
              widgets,
              remaining_inventories_ids,
              queryParamsObject: { ...requestParams },
            },
          });
        }
      })
      .catch(() => {
        dispatch({
          type: 'pd-category-widget-fail',
        });
      });
  };

export const fetchDynamicFooterLinks = (params: any) => (dispatch: Dispatch) =>
  getDynamicFooterLinks(params)
    .then((response) => {
      dispatch({
        type: footerActionTypes.FETCH_DYNAMIC_FOOTER_LINKS,
        payload: response,
      });
      return response;
    })
    .catch((err) => {
      logger.error(err, DYNAMIC_FOOTER_LINK_API_ERR_MESSAGE);
    });
